import { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material"
import { useForm } from "react-hook-form";
import { BackdropComponent, Controls } from "../../../components";
import { theme } from "../../../utils/theme/godzilla_light";
import { monthOptions, yearOptions } from "../../../utils/date.utils";
import { useTranslation } from "react-i18next";
import { IInfoComponentProps } from "../../../types/editProfile.types";
import { formSchemaResolver, formDefaults } from "../../onboard/professionalInformation/professionalInfo.helper";
import DialogModalHOC from "../components/DialogModalHOC";
import AutoSearch from "../../../components/controls/autoSearch/AutoSearch";
import InfoTransMessage from '../../onboard/professionalInformation/transMessage';
import TransMessage from '../transMessage';
import { useMutation, useQuery } from "react-query";
import { getPrimarySpecialtyRefURL, getQueryData, withCredentials, instance as axiosInstance, updateOnboardingStatusURL } from "../../../services";
import { filterMonthDataById, modifyCommonDropDownData } from "../../common.helper";
import { useAppContext, useSnackbar } from "../../../contexts";
import { formatPreferredCommunication } from "./personalnfo.helper";
import { validateErrorMsg } from "../../../utils/error.utils";

const communicationOptionsData = [
    { label: "Email", id: 'EMAIL' },
    { label: "Phone", id: 'PHONE' },
    { label: "SMS (Text)", id: 'SMS' },
]

const EditProfessionalInfo = (props: IInfoComponentProps) => {
    const { isVisible, onCloseDialog, data } = props
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down(950))
    const { providerId } = useAppContext();
    const { showSnackbar } = useSnackbar();

    const [specialtyListData, setSpecialtyListData] = useState<any[]>([])
    const [formSubmitLoading, setFormSubmitLoading] = useState(false)

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset: formReset,
        getValues
    } = useForm<any>({
        resolver: formSchemaResolver(t),
        defaultValues: formDefaults
    });
    const npiValue = getValues("national_provider_id")
    const { data: specialtyRefList, isLoading: specialtyDataLoading } = useQuery("primarySpecialtyRefListData", async () =>
        getQueryData(getPrimarySpecialtyRefURL, withCredentials())
    );

    useEffect(() => {
        if (specialtyRefList?.data?.length) {
            const formatedSpecialty = modifyCommonDropDownData(specialtyRefList?.data)
            setSpecialtyListData(formatedSpecialty)
        }
    }, [specialtyRefList])

    useEffect(() => {
        const editProfessionalformValues = { ...formDefaults };
        const professionalPrefillData = data
        const specialtyInfoData = professionalPrefillData?.specialties?.length ? professionalPrefillData.specialties.filter((item: any) => item.type === "PRIMARY") : []
        const prefillComType: any = professionalPrefillData?.prefCommMethod ? communicationOptionsData.filter((el) => el.id === professionalPrefillData?.prefCommMethod) : communicationOptionsData

        editProfessionalformValues.primary_specialty = specialtyInfoData?.length > 0 ? specialtyInfoData[0].name : ''
        editProfessionalformValues.national_provider_id = professionalPrefillData?.npi
        editProfessionalformValues.practice_year = specialtyInfoData?.length > 0 ? specialtyInfoData[0]?.year?.toString() : ''
        editProfessionalformValues.practice_month = filterMonthDataById(specialtyInfoData[0]?.month, 'id')
        editProfessionalformValues.preffered_communication = prefillComType[0].label
        formReset(editProfessionalformValues)
    }, [data, formReset])

    const submitProfessionalInfoMutation = useMutation((params: any) => {
        setFormSubmitLoading(true)
        return axiosInstance().put(updateOnboardingStatusURL(providerId), params, withCredentials())
    },
        {
            onSuccess: () => {
                if (onCloseDialog) {
                    onCloseDialog()
                }
            },
            onError: (error: any) => {
                showSnackbar(validateErrorMsg(error?.data?.detail), "error")
            },
            onSettled: () => {
                setFormSubmitLoading(false)
            }
        }
    );

    const onSaveProfessionalInfo = (professionalFormValues: any) => {
        const prefCommType = formatPreferredCommunication(professionalFormValues?.preffered_communication)
        const professionalParams = {
            "npi": professionalFormValues?.national_provider_id,
            "prefCommMethod": prefCommType,
            "specialties": [
                {
                    "name": professionalFormValues.primary_specialty,
                    "type": "PRIMARY",
                    "year": professionalFormValues.practice_year,
                    "month": filterMonthDataById(professionalFormValues.practice_month, 'label')
                }
            ]
        }
        submitProfessionalInfoMutation.mutate(professionalParams)
    }

    return (
        <DialogModalHOC
            boxTitle={t(InfoTransMessage.PAGE_INFO_TITLE)}
            isOpen={isVisible}
            handleSave={handleSubmit(onSaveProfessionalInfo)}
            onClose={onCloseDialog}>
            <Box component={'form'}
                sx={{
                    mt: "1.5rem",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column"
                }}>
                <AutoSearch
                    control={control}
                    name='primary_specialty'
                    label={t(InfoTransMessage.PRIMARY_SPECIALTY)}
                    options={specialtyListData}
                    defaultValue={''}
                    error={errors.primary_specialty || ''}
                    required
                />
                <Controls.Input
                    name="national_provider_id"
                    label={t(InfoTransMessage.NPI_LABEL)}
                    defaultValue={npiValue}
                    infoIcon
                    infoContent={t(TransMessage.TOOLTIP_NPI)}
                    control={control}
                    disabled
                    labelStyles={{ pr: "10px" }}
                />
                <Typography sx={{
                    ...theme.typography.components.inputLabel, textAlign: "left", mt: '12px'
                }}>
                    {t(InfoTransMessage.PRACTICE_DATE_INFO)}
                </Typography>
                <Typography sx={{
                    ...theme.typography.components.captionsText
                }}>{t(InfoTransMessage.PRACTICE_DATES)}</Typography>
                <Box display="flex" gap="12px">
                    <Box width={isMobile ? "60%" : "40%"}>
                        <AutoSearch
                            control={control}
                            name="practice_month"
                            label=""
                            options={monthOptions}
                            defaultValue=""
                            error={errors.practice_month || ""}
                            required
                        />
                    </Box>
                    <Box width={isMobile ? "40%" : "50%"} sx={{ maxWidth: isMobile ? "none" : "120px" }}>
                        <AutoSearch
                            control={control}
                            name="practice_year"
                            label=""
                            options={yearOptions}
                            defaultValue=""
                            error={errors.practice_year || ""}
                            required
                        />
                    </Box>
                </Box>

                <AutoSearch
                    control={control}
                    name='preffered_communication'
                    label={t(InfoTransMessage.COMMUNICATION_TYPE)}
                    options={communicationOptionsData}
                    defaultValue={communicationOptionsData[0].label}
                    sx={{ mt: '12px' }}
                    error={errors.preffered_communication || ''}
                    required
                />
            </Box>
            <BackdropComponent showBackdrop={specialtyDataLoading || formSubmitLoading} />
        </DialogModalHOC>
    )
}

export default EditProfessionalInfo